<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-4xl mx-auto w-full px-4 py-8">
      <div v-if="needsPresentialComplete" class="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm flex flex-wrap items-center gap-2">
        <span>Complete seu endereço de atuação em Meu Perfil para que clientes possam te encontrar.</span>
        <router-link to="/perfil" class="font-semibold underline hover:no-underline">Meu Perfil</router-link>
      </div>
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-[#152a34]">Meus Serviços</h1>
        <button @click="openForm(null)" class="px-5 py-2 bg-[#b99a52] text-white rounded-xl font-semibold hover:bg-[#a08840] transition flex items-center gap-2">
          <span class="mdi mdi-plus"></span> Novo Serviço
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="i in 4" :key="i" class="bg-white rounded-2xl shadow p-5 animate-pulse h-32"></div>
      </div>

      <!-- Vazio -->
      <div v-else-if="services.length === 0" class="text-center py-20 text-gray-400">
        <span class="mdi mdi-briefcase-outline text-5xl block mb-3"></span>
        <p>Nenhum serviço cadastrado.</p>
        <p class="text-sm mt-1">Adicione seus serviços para que os contratantes possam te encontrar.</p>
      </div>

      <!-- Lista -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="service in services"
          :key="service.id"
          class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 flex flex-col"
          :class="{ 'opacity-60': !service.is_active }"
        >
          <div class="flex items-start justify-between gap-3 mb-2">
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-[#152a34]">{{ service.name }}</h3>
              <p v-if="service.category" class="text-xs text-[#b99a52]">{{ service.category.description }}</p>
            </div>
            <span v-if="!service.is_active" class="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inativo</span>
          </div>
          <p v-if="service.description" class="text-sm text-gray-500 mb-3 flex-1">{{ service.description }}</p>
          <div class="flex items-end justify-between mt-auto">
            <div>
              <p class="text-xs text-gray-400 mb-0.5">Preço base</p>
              <p class="text-lg font-bold text-[#152a34]">R$ {{ formatPrice(service.price) }}</p>
              <p class="text-xs text-gray-400 mt-1">Com taxa</p>
              <p class="text-base font-semibold text-[#b99a52]">R$ {{ formatPrice(service.price_with_fee || calcPriceWithFee(service.price, service.fee_percentage)) }}</p>
              <p class="text-xs text-gray-400">{{ service.duration_minutes }} min</p>
            </div>
            <div class="flex gap-2">
              <button @click="openForm(service)" class="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition" title="Editar">
                <span class="mdi mdi-pencil text-[#152a34]"></span>
              </button>
              <button @click="toggleActive(service)" class="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition" :title="service.is_active ? 'Desativar' : 'Ativar'">
                <span class="mdi" :class="service.is_active ? 'mdi-eye text-green-500' : 'mdi-eye-off text-gray-400'"></span>
              </button>
              <button @click="openDeleteModal(service)" class="p-2 rounded-lg bg-gray-50 hover:bg-red-50 transition" title="Excluir">
                <span class="mdi mdi-delete text-red-400"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de form (persistente) -->
    <div v-if="showModal" class="fixed inset-0 bg-[#152a34]/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
        <h2 class="text-lg font-bold text-[#152a34] mb-4">{{ editingService ? 'Editar Serviço' : 'Novo Serviço' }}</h2>

        <div class="space-y-4">
          <div>
            <label class="text-sm font-medium block mb-1">Nome do Serviço *</label>
            <input v-model="form.name" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
          </div>
          <div>
            <label class="text-sm font-medium block mb-1">Categoria *</label>
            <div class="relative">
              <input
                :value="categorySearch"
                @input="onCategorySearch"
                type="text"
                placeholder="Buscar categoria presencial..."
                class="w-full border rounded-xl px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]"
                :class="!form.category_id && formError ? 'border-red-300' : 'border-gray-200'"
              />
              <span v-if="categoryLoading" class="mdi mdi-loading animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#b99a52] text-base pointer-events-none"></span>
              <div
                ref="categoryDropdownRef"
                v-if="categoryLoading || categoryOptions.length"
                class="absolute top-full mt-1 left-0 right-0 bg-white border rounded-xl shadow-lg z-10 max-h-40 overflow-auto"
                :style="categoryDropdownStyle"
              >
                <div v-if="categoryLoading" class="px-3 py-3 flex items-center gap-2 text-sm text-gray-400">
                  <span class="mdi mdi-loading animate-spin text-base shrink-0"></span>
                  <span>Buscando categorias...</span>
                </div>
                <template v-else>
                  <button v-for="opt in categoryOptions" :key="opt.id" type="button" @click="selectCategory(opt)"
                    class="w-full text-left px-3 py-2 hover:bg-gray-50 active:bg-gray-100 text-sm border-b border-gray-100 last:border-0">
                    {{ opt.description }}
                  </button>
                </template>
              </div>
            </div>
            <p v-if="!form.category_id && formError" class="mt-1 text-xs text-red-600">Selecione uma categoria.</p>
            <div class="mt-2 p-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900">
              <p class="text-xs font-semibold flex items-center gap-1">
                <span class="mdi mdi-alert-circle-outline text-base"></span>
                Não encontrou sua categoria?
              </p>
              <p class="text-xs mt-1 leading-5">
                Envie um e-mail para
                <a class="font-bold underline hover:no-underline" href="mailto:contato@getlance.com.br">contato@getlance.com.br</a>
                informando o nome da categoria desejada para nossa equipe avaliar o cadastro.
              </p>
            </div>
          </div>
          <div>
            <label class="text-sm font-medium block mb-1">Descrição</label>
            <textarea v-model="form.description" rows="2" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#b99a52]"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-medium block mb-1">Preço (R$) *</label>
              <input v-model="priceFormatted" @input="formatPriceInput" type="text" inputmode="numeric" placeholder="R$ 0,00" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Duração *</label>
              <select v-model="form.duration_minutes" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]">
                <option value="30">30 min</option>
                <option value="60">1 hora</option>
                <option value="90">1h 30min</option>
                <option value="120">2 horas</option>
                <option value="180">3 horas</option>
                <option value="240">4 horas</option>
                <option value="300">5 horas</option>
                <option value="360">6 horas</option>
              </select>
            </div>
          </div>

          <!-- Preço com taxa -->
          <div v-if="form.price > 0" class="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Preço base</span>
              <span class="font-medium text-sm">R$ {{ formatPrice(form.price) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <!--<span class="text-sm text-gray-600">Taxa ({{ FEE_PERCENTAGE }}%)</span>-->
              <span class="text-sm text-gray-600">Taxa</span>
              <span class="text-sm text-amber-700">+ R$ {{ formatPrice(feeAmount) }}</span>
            </div>
            <div class="border-t border-amber-200 pt-2 flex items-center justify-between">
              <span class="text-sm font-semibold text-[#152a34]">Valor cobrado do cliente</span>
              <span class="font-bold text-[#152a34]">R$ {{ formatPrice(priceWithFee) }}</span>
            </div>
          </div>
        </div>

        <div v-if="formError" class="mt-3 p-3 bg-red-50 text-red-600 rounded-xl text-xs">{{ formError }}</div>

        <div class="flex gap-3 mt-5">
          <button @click="showModal = false" class="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm hover:bg-gray-50">Cancelar</button>
          <button @click="saveService" :disabled="saving" class="flex-1 py-2.5 bg-[#b99a52] text-white rounded-xl text-sm font-semibold hover:bg-[#a08840] disabled:opacity-50">
            <span v-if="saving" class="mdi mdi-loading animate-spin mr-1"></span>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmação de exclusão -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-[#152a34]/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
        <div class="flex items-center gap-3 mb-4">
          <span class="mdi mdi-delete-alert text-3xl text-red-500"></span>
          <h3 class="font-bold text-[#152a34] text-lg">Excluir Serviço</h3>
        </div>
        <p class="text-sm text-gray-600 mb-2">
          Tem certeza que deseja excluir o serviço <strong>{{ serviceToDelete?.name }}</strong>?
        </p>
        <p class="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-5">
          <span class="mdi mdi-information-outline mr-1"></span>
          Se houver agendamentos ativos vinculados, o serviço será desativado automaticamente em vez de excluído.
        </p>
        <div class="flex gap-3">
          <button @click="showDeleteModal = false" class="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm hover:bg-gray-50 transition">Cancelar</button>
          <button @click="confirmDelete" :disabled="deleting" class="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition disabled:opacity-50">
            <span v-if="deleting" class="mdi mdi-loading animate-spin mr-1"></span>
            Excluir
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import api, { listMyServices, createService, updateService, deleteService as apiDeleteService } from '@/services/api'
import { usePresentialProfile } from '@/composables/usePresentialProfile'
import { useProviderOnboardingStore } from '@/store/providerOnboardingStore'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const FEE_PERCENTAGE = 7
const onboardingStore = useProviderOnboardingStore()

const services = ref([])
const loading = ref(true)
const needsPresentialComplete = ref(false)
const showModal = ref(false)
const editingService = ref(null)
const saving = ref(false)
const formError = ref('')

const showDeleteModal = ref(false)
const serviceToDelete = ref(null)
const deleting = ref(false)

const categorySearch = ref('')
const categoryOptions = ref([])
const categoryLoading = ref(false)
const categoryDropdownRef = ref(null)
const categoryDropdownStyle = ref({})
const priceFormatted = ref('')

function clipDropdownToViewport (dropdownEl, styleRef) {
  if (!dropdownEl) return
  nextTick(() => {
    const vvHeight = window.visualViewport?.height ?? window.innerHeight
    const rect = dropdownEl.getBoundingClientRect()
    const available = vvHeight - rect.top - 8
    if (available > 0 && available < rect.height) {
      styleRef.value = { maxHeight: Math.max(available, 80) + 'px' }
    } else {
      styleRef.value = {}
    }
  })
}

const form = ref({ name: '', description: '', price: null, duration_minutes: 60, category_id: null })

let categoryDebounce = null

const feeAmount = computed(() => {
  const p = parseFloat(form.value.price) || 0
  return parseFloat((p * FEE_PERCENTAGE / 100).toFixed(2))
})

const priceWithFee = computed(() => {
  const p = parseFloat(form.value.price) || 0
  return parseFloat((p + feeAmount.value).toFixed(2))
})

function formatPrice (p) { return Number(p || 0).toFixed(2).replace('.', ',') }

function calcPriceWithFee (price, fee) {
  const p = parseFloat(price) || 0
  const f = parseFloat(fee) || FEE_PERCENTAGE
  return parseFloat((p * (1 + f / 100)).toFixed(2))
}

function formatPriceInput (event) {
  const clean = event.target.value.replace(/\D/g, '')
  const num = parseInt(clean, 10) / 100
  form.value.price = isNaN(num) ? null : num
  priceFormatted.value = form.value.price
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(form.value.price)
    : ''
}

function openForm (service) {
  editingService.value = service
  if (service) {
    form.value = {
      name: service.name,
      description: service.description || '',
      price: Number(service.price),
      duration_minutes: service.duration_minutes,
      category_id: service.category_id
    }
    priceFormatted.value = form.value.price
      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(form.value.price)
      : ''
    categorySearch.value = service.category?.description || ''
  } else {
    form.value = { name: '', description: '', price: null, duration_minutes: 60, category_id: null }
    priceFormatted.value = ''
    categorySearch.value = ''
  }
  formError.value = ''
  showModal.value = true
}

function openDeleteModal (service) {
  serviceToDelete.value = service
  showDeleteModal.value = true
}

function onCategorySearch (event) {
  if (event?.target) categorySearch.value = event.target.value
  clearTimeout(categoryDebounce)
  form.value.category_id = null
  if (!categorySearch.value.trim()) {
    categoryOptions.value = []
    categoryLoading.value = false
    return
  }
  categoryLoading.value = true
  categoryDebounce = setTimeout(async () => {
    try {
      const { data } = await api.get('/categories', { params: { search: categorySearch.value, limit: 10, type: 'presential' } })
      categoryOptions.value = data.data || []
      if (categoryOptions.value.length) clipDropdownToViewport(categoryDropdownRef.value, categoryDropdownStyle)
    } catch {
      categoryOptions.value = []
    } finally {
      categoryLoading.value = false
    }
  }, 300)
}

function selectCategory (opt) {
  form.value.category_id = opt.id
  categorySearch.value = opt.description
  categoryOptions.value = []
  categoryDropdownStyle.value = {}
}

async function saveService () {
  formError.value = ''
  if (!form.value.name || !form.value.price) { formError.value = 'Nome e preço são obrigatórios.'; return }
  if (!form.value.category_id) { formError.value = 'Categoria é obrigatória.'; return }
  saving.value = true
  try {
    const payload = { ...form.value, fee_percentage: FEE_PERCENTAGE }
    if (editingService.value) {
      await updateService(editingService.value.id, payload)
    } else {
      await createService(payload)
    }
    showModal.value = false
    await load()
    onboardingStore.refresh()
  } catch (err) {
    formError.value = err?.response?.data?.error || 'Erro ao salvar.'
  } finally {
    saving.value = false
  }
}

async function toggleActive (service) {
  try {
    await updateService(service.id, { is_active: !service.is_active })
    service.is_active = !service.is_active
  } catch {}
}

async function confirmDelete () {
  if (!serviceToDelete.value) return
  deleting.value = true
  try {
    await apiDeleteService(serviceToDelete.value.id)
    showDeleteModal.value = false
    serviceToDelete.value = null
    await load()
    onboardingStore.refresh()
  } catch (err) {
    showDeleteModal.value = false
    formError.value = err?.response?.data?.error || 'Erro ao excluir.'
  } finally {
    deleting.value = false
  }
}

async function load () {
  loading.value = true
  try {
    const { data } = await listMyServices()
    services.value = data.data || []
  } catch { services.value = [] }
  finally { loading.value = false }
}

async function checkPresentialProfile () {
  try {
    const { data } = await api.get('/users/profile')
    const user = data.data || data
    const { needsLocation } = usePresentialProfile(user)
    needsPresentialComplete.value = needsLocation
  } catch {
    needsPresentialComplete.value = false
  }
}

onMounted(() => {
  load()
  checkPresentialProfile()
})
</script>
